import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Sparkles, ArrowLeft, ChevronRight, Heart, Music, Share2, Sun, Instagram,
} from "lucide-react";
import {
  getPaymentStatus, getCompatibilityReading, getCompatibilityWheelUrl, getCompatibilityShareCardUrl,
} from "@/lib/api";
import { trackPurchase } from "@/lib/tracking";
import { SITE_URL } from "@/lib/site";
import { toast } from "sonner";
import { useReadingGate } from "@/components/earthpulse/ReadingGate";
import SocialShareButtons from "@/components/earthpulse/SocialShareButtons";

const POLL_INTERVAL = 2000;
const MAX_POLLS = 30;

/**
 * The paid two-person compatibility reading. Polls payments/status until
 * paid, then fetches the reading + shows the dual natal wheel.
 */
export default function CompatibilityReading() {
  const gateEl = useReadingGate("compatibility reading");
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | ready | error
  const [reading, setReading] = useState(null);
  const [error, setError] = useState(null);
  const purchaseFiredRef = useRef(false);

  const load = useCallback(async () => {
    if (!sessionId) return;
    let attempts = 0;
    while (attempts < MAX_POLLS) {
      try {
        const s = await getPaymentStatus(sessionId);
        if (s.payment_status === "paid") {
          const r = await getCompatibilityReading(sessionId);
          setReading(r);
          setState("ready");
          if (!purchaseFiredRef.current) {
            purchaseFiredRef.current = true;
            const value = (r?.pricing?.amount_pence ?? 900) / 100;
            trackPurchase({ value, contentId: "cosmic_compatibility" });
          }
          return;
        }
        if (["failed", "expired"].includes(s.payment_status)) {
          setError(`Payment ${s.payment_status}. Please try again.`);
          setState("error");
          return;
        }
      } catch (e) {
        if (e?.response?.status === 404) {
          setError("This reading session doesn't exist or has expired.");
          setState("error");
          return;
        }
      }
      attempts += 1;
      await new Promise((r) => setTimeout(r, POLL_INTERVAL));
    }
    setError("Timed out confirming payment. If you were charged, refresh in a moment.");
    setState("error");
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  // Membership check FIRST — non-subscribers see the paywall, never the reading.
  if (gateEl) return gateEl;

  if (!sessionId) {
    return (
      <FullPageMessage message="No reading session found." link="/compatibility" linkLabel="Start a new reading" />
    );
  }
  if (state === "polling") return <Loading />;
  if (state === "error") {
    return (
      <FullPageMessage message={error} link="/compatibility" linkLabel="Try again" isError showResend />
    );
  }

  const a = reading?.person_a || {};
  const b = reading?.person_b || {};
  const r = reading?.reading || {};
  const chart_a = reading?.chart_a || {};
  const chart_b = reading?.chart_b || {};
  const syn = reading?.synastry || {};
  const relationship = reading?.relationship || "partners";
  const wheelUrl = getCompatibilityWheelUrl(sessionId);

  const share = async () => {
    // Always share the canonical production URL — never the preview host
    const url = `${SITE_URL}/compatibility/reading?session_id=${encodeURIComponent(sessionId || "")}`;
    const title = `Earth · Pulse — ${a.name || "us"} & ${b.name || "them"}`;
    if (navigator.share) {
      try { await navigator.share({ title, url, text: r.headline || "" }); }
      catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied — paste it anywhere");
      } catch {
        toast.error("Couldn't copy — long-press the address bar instead");
      }
    }
  };

  const shareImage = async () => {
    const cardUrl = getCompatibilityShareCardUrl(sessionId);
    const filename = `earthpulse-${(a.name || "us").toLowerCase()}-and-${(b.name || "them").toLowerCase()}.png`;
    try {
      const res = await fetch(cardUrl);
      if (!res.ok) throw new Error("card fetch failed");
      const blob = await res.blob();
      // Backend returns lossless PNG (3× resolution, ~300 DPI).
      const file = new File([blob], filename, { type: "image/png" });

      // Native mobile share (one tap → Instagram / Reels / anywhere)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `${a.name} & ${b.name} · Earth · Pulse`,
          text: r.headline ? `"${r.headline}" — earthpulse.store` : "earthpulse.store",
        });
        return;
      }

      // Fallback: trigger a download
      const objUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objUrl);
      toast.success("Image saved — post it to your reel");
    } catch (e) {
      // Ultimate fallback: open in a new tab so the user can long-press to save
      window.open(cardUrl, "_blank");
    }
  };

  return (
    <div className="min-h-screen relative z-10" data-testid="compat-reading-page">
      <header className="border-b border-[color:var(--gold)]/15">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="compat-back">
            <ArrowLeft size={16} className="text-[color:var(--gold)] group-hover:-translate-x-0.5 transition-transform" />
            <span className="label-overline">Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <Heart size={16} className="text-[color:var(--rose-gold)]" />
            <span className="font-serif-display text-white text-lg">How your charts meet</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] px-6 md:px-10 py-14 md:py-20 space-y-10">
        {/* Names + headline */}
        <section className="text-center max-w-3xl mx-auto stagger-in">
          <div className="label-overline">{relationship}</div>
          <h1 className="mt-5 font-serif-display text-[color:var(--parchment)] text-4xl md:text-6xl leading-[1.02] tracking-tight">
            {a.name} <span className="font-serif-italic text-[color:var(--rose-gold)]">&amp;</span> {b.name}
          </h1>
          {r.headline && (
            <p className="mt-6 font-serif-italic text-[color:var(--gold)] text-lg md:text-xl leading-relaxed">
              &ldquo;{r.headline}&rdquo;
            </p>
          )}
          {reading?.birth_data?.email && (
            <div
              className="mt-8 mx-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[color:var(--gold)]/40 font-serif-italic text-[color:var(--parchment-soft)]"
              style={{ background: "rgba(212,162,76,0.06)" }}
            >
              {reading.email_sent
                ? <>Reading sent to <strong className="text-[color:var(--parchment)] not-italic">{reading.birth_data.email}</strong></>
                : <>Delivered here on screen · optional email {reading.birth_data.email}</>}
            </div>
          )}
        </section>

        {/* Score + share */}
        <section className="parchment-card rounded-3xl p-8 md:p-12 stagger-in" data-testid="compat-score">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            <div className="text-center md:text-left">
              <div className="label-overline">Harmony index</div>
              <div className="mt-2 font-serif-display text-[color:var(--parchment)] text-6xl md:text-7xl leading-none tracking-tight">
                {syn.overall_score}
                <span className="text-[color:var(--gold)] text-2xl md:text-3xl">/100</span>
              </div>
              <p className="mt-3 font-serif-italic text-[color:var(--text-secondary)] text-sm">
                A blend of element chemistry and cross-chart aspects — never a verdict, just a pattern.
              </p>
            </div>
            <div className="md:col-span-2 grid grid-cols-2 gap-4">
              <MiniStat
                label={`${a.name || "A"} · sun`}
                value={chart_a?.sun?.sign || "—"}
                sub={chart_a?.sun?.element}
              />
              <MiniStat
                label={`${b.name || "B"} · sun`}
                value={chart_b?.sun?.sign || "—"}
                sub={chart_b?.sun?.element}
              />
              <MiniStat
                label="Sun-sun match"
                value={`${syn.sun_element_score ?? "—"}`}
                sub="element chemistry"
              />
              <MiniStat
                label="Moon-moon match"
                value={`${syn.moon_element_score ?? "—"}`}
                sub={syn.moon_element_score ? "emotional resonance" : "need both times"}
              />
            </div>
          </div>
          <div className="mt-8 flex flex-wrap gap-3 justify-center md:justify-start">
            <button
              type="button"
              onClick={shareImage}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--rose-gold)] hover:brightness-110 text-[color:var(--bg-base)] font-medium transition"
              data-testid="compat-share-image"
              title="Save the square image or share to Instagram / Reels / TikTok"
            >
              <Instagram size={14} /> Post to reel
            </button>
            <button
              type="button"
              onClick={share}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium transition"
              data-testid="compat-share"
            >
              <Share2 size={14} /> Share this reading
            </button>
          </div>
          <div className="mt-5 flex justify-center md:justify-start">
            <SocialShareButtons
              url="https://earthpulse.store/"
              text={`Our Compatibility reading on Earth Pulse is uncannily accurate. Two charts, one honest map. ✨`}
              variant="compact"
              testIdPrefix="compat-social-share"
            />
          </div>
        </section>

        {/* Overview prose */}
        {r.overview && (
          <section className="parchment-card rounded-2xl p-8 md:p-12 stagger-in" data-testid="compat-overview">
            <div className="label-overline">The two of you</div>
            <p className="mt-5 text-[color:var(--parchment-soft)] text-lg md:text-xl leading-relaxed font-sans-body">
              {r.overview}
            </p>
          </section>
        )}

        {/* Dual wheel */}
        <section className="stagger-in">
          <div className="text-center mb-6">
            <div className="label-overline">Your dual natal wheel</div>
            <h2 className="mt-3 font-serif-display text-[color:var(--parchment)] text-3xl md:text-4xl tracking-tight">
              Two skies, one photograph.
            </h2>
          </div>
          <div className="parchment-card rounded-2xl p-4 md:p-8 flex flex-col items-center gap-5" data-testid="compat-wheel-block">
            <img
              src={wheelUrl}
              alt={`Natal wheels for ${a.name} and ${b.name}`}
              className="w-full max-w-[900px] rounded-xl"
              data-testid="compat-wheel-image"
            />
            <p className="font-serif-italic text-[color:var(--text-secondary)] text-sm text-center max-w-md">
              A moment in time — screenshot the wheel to keep or share.
            </p>
          </div>
        </section>

        {/* 3-part narrative */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger-in">
          <NarrativeCard label="How you meet" body={r.how_you_meet} accent="gold" />
          <NarrativeCard label="How you love" body={r.how_you_love} accent="rose" />
          <NarrativeCard label="How you clash" body={r.how_you_clash} accent="gold" />
        </div>

        {/* Tender practice */}
        {(r.practice_for_a || r.practice_for_b || r.shared_ritual) && (
          <section className="parchment-card rounded-2xl p-8 md:p-12 stagger-in" data-testid="compat-practice">
            <div className="label-overline">A tender practice</div>
            <div className="mt-6 space-y-4">
              {r.practice_for_a && (
                <p className="text-[color:var(--parchment-soft)] leading-relaxed font-sans-body">
                  <strong className="text-[color:var(--parchment)] font-serif-display text-xl">{a.name}: </strong>
                  <span className="font-serif-italic">{r.practice_for_a}</span>
                </p>
              )}
              {r.practice_for_b && (
                <p className="text-[color:var(--parchment-soft)] leading-relaxed font-sans-body">
                  <strong className="text-[color:var(--parchment)] font-serif-display text-xl">{b.name}: </strong>
                  <span className="font-serif-italic">{r.practice_for_b}</span>
                </p>
              )}
              {r.shared_ritual && (
                <div className="mt-6 pt-6 border-t border-[color:var(--gold)]/15">
                  <div className="label-overline">Shared ritual</div>
                  <p className="mt-3 font-serif-display text-[color:var(--parchment)] text-xl leading-snug">
                    {r.shared_ritual}
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Song */}
        {r.song_for_you && r.song_for_you.title && (
          <section className="parchment-card rounded-2xl p-8 md:p-12 text-center stagger-in" data-testid="compat-song">
            <Music size={18} className="mx-auto text-[color:var(--rose-gold)]" />
            <div className="mt-4 label-overline">A song for you two</div>
            <p className="mt-6 font-serif-display text-[color:var(--parchment)] text-3xl md:text-4xl tracking-tight leading-snug">
              &ldquo;{r.song_for_you.title}&rdquo;
            </p>
            <p className="mt-3 font-serif-italic text-[color:var(--rose-gold)] text-lg">
              {r.song_for_you.artist}
            </p>
            {r.song_for_you.why && (
              <p className="mt-4 font-serif-italic text-[color:var(--text-muted)] text-sm max-w-md mx-auto">
                {r.song_for_you.why}
              </p>
            )}
          </section>
        )}

        {/* Aspect table */}
        {syn.aspects?.length > 0 && (
          <section className="parchment-card rounded-2xl p-8 md:p-10 stagger-in" data-testid="compat-aspects">
            <div className="label-overline">The classical aspects between you</div>
            <p className="mt-3 font-serif-italic text-[color:var(--text-secondary)] text-sm">
              Angles between your planets — how the two charts speak to each other in traditional synastry.
            </p>
            <ul className="mt-6 divide-y divide-[color:var(--gold)]/15">
              {syn.aspects.map((asp, i) => (
                <li key={i} className="py-3 flex items-center justify-between gap-4">
                  <div>
                    <div className="font-serif-display text-[color:var(--parchment)] text-base">{asp.between}</div>
                    <div className="font-serif-italic text-[color:var(--text-secondary)] text-sm">{asp.blurb}</div>
                  </div>
                  <div className="text-right">
                    <div className="label-overline">{asp.aspect}</div>
                    <div className="font-serif-italic text-[color:var(--text-muted)] text-xs">
                      orb {asp.orb_delta.toFixed(1)}°
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="pt-6 border-t border-[color:var(--gold)]/15 flex flex-wrap items-center justify-between gap-4">
          <Link
            to="/birth-chart"
            className="inline-flex items-center gap-2 font-serif-italic text-[color:var(--gold)] hover:underline"
          >
            <Sun size={14} /> Get your individual birth chart
          </Link>
          <Link
            to="/almanac"
            className="inline-flex items-center gap-2 font-serif-italic text-[color:var(--gold)] hover:underline"
          >
            Return to daily guidance <ChevronRight size={14} />
          </Link>
        </div>
      </main>
    </div>
  );
}

function MiniStat({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-[color:var(--gold)]/20 p-4">
      <div className="label-overline text-[10px]">{label}</div>
      <div className="mt-1 font-serif-display text-[color:var(--parchment)] text-2xl">{value}</div>
      <div className="font-serif-italic text-[color:var(--text-muted)] text-xs">{sub}</div>
    </div>
  );
}

function NarrativeCard({ label, body, accent }) {
  if (!body) return null;
  const color = accent === "rose" ? "var(--rose-gold)" : "var(--gold)";
  return (
    <div className="parchment-card rounded-2xl p-8">
      <div className="label-overline" style={{ color }}>{label}</div>
      <p className="mt-5 text-[color:var(--parchment-soft)] leading-relaxed font-sans-body">{body}</p>
    </div>
  );
}

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <div className="mx-auto w-14 h-14 rounded-full border border-[color:var(--gold)]/50 flex items-center justify-center animate-pulse">
          <Sparkles size={22} className="text-[color:var(--gold)]" />
        </div>
        <h1 className="mt-8 font-serif-display text-3xl md:text-4xl text-[color:var(--parchment)] tracking-tight">
          Reading how your charts meet…
        </h1>
        <p className="mt-4 font-serif-italic text-[color:var(--text-secondary)]">
          Confirming payment and calculating synastry. This takes a few seconds.
        </p>
      </div>
    </div>
  );
}

function FullPageMessage({ message, link, linkLabel, isError, showResend }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        {isError && <Sparkles size={22} className="text-[color:var(--gold)] mx-auto mb-4" />}
        {isError && (
          <h1 className="font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl mb-3">
            We couldn&apos;t open this reading.
          </h1>
        )}
        <p
          className={`font-serif-italic mb-6 ${isError ? "text-[color:var(--parchment-soft)]" : "text-[color:var(--parchment-soft)]"}`}
          data-testid="compat-message"
        >
          {message}
        </p>
        <div className="space-y-3">
          {showResend && (
            <Link
              to="/my-readings"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium transition"
              data-testid="compat-error-resend"
            >
              Re-send my reading links
            </Link>
          )}
          <Link
            to={link}
            className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full ${
              showResend
                ? "border border-[color:var(--gold)]/40 text-[color:var(--parchment)] hover:bg-[color:var(--gold)]/10"
                : "text-[color:var(--gold)] hover:underline"
            } transition`}
          >
            {linkLabel} <ChevronRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
